import React from 'react';
import { View, StyleSheet } from 'react-native';
import Svg, { Circle, Line, Path, Text as SvgText } from 'react-native-svg';
import ChartStyles from './chartStyles';
import Legend from './Legend';

interface WindroseProps {
  data: Record<string, number[]>;
  directionBins: { label: string; start: number; end: number }[];
  speedRanges: { start: number; end: number; color: string }[];
  /** Container width in px — drives SVG size. Defaults to 400. */
  width?: number;
}

function polarXY(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function sectorPath(
  cx: number, cy: number,
  innerR: number, outerR: number,
  startDeg: number, endDeg: number
): string {
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  const s1 = polarXY(cx, cy, outerR, startDeg);
  const e1 = polarXY(cx, cy, outerR, endDeg);
  const s2 = polarXY(cx, cy, innerR, endDeg);
  const e2 = polarXY(cx, cy, innerR, startDeg);
  if (innerR <= 0.5) {
    return [`M ${cx} ${cy}`, `L ${s1.x} ${s1.y}`, `A ${outerR} ${outerR} 0 ${largeArc} 1 ${e1.x} ${e1.y}`, 'Z'].join(' ');
  }
  return [
    `M ${s1.x} ${s1.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${e1.x} ${e1.y}`,
    `L ${s2.x} ${s2.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${e2.x} ${e2.y}`,
    'Z',
  ].join(' ');
}

export default function Windrose({ data, directionBins, speedRanges, width: propWidth = 400 }: WindroseProps) {
  // Cap at 480 so it doesn't get unwieldy on very wide screens; scale down on narrow screens
  const size = Math.min(Math.max(propWidth - 16, 240), 480);
  const radius = size * 0.33;
  const labelOffset = size * 0.045;
  const cx = size / 2;
  const cy = size / 2;
  const numDirs = directionBins.length;
  const angleStep = 360 / numDirs;
  const labelFontSize = Math.max(7, size * 0.022);
  const ringFontSize = Math.max(6, size * 0.018);

  const totalObs = Object.values(data).reduce(
    (sum, arr) => sum + arr.reduce((a, b) => a + b, 0), 0
  );
  const dirFreqs = directionBins.map(bin =>
    (data[bin.label] || []).reduce((a, b) => a + b, 0) / (totalObs || 1)
  );
  const maxDirFreq = Math.max(...dirFreqs, 0.01);

  const ringCount = 4;
  const rings = Array.from({ length: ringCount }, (_, i) => (i + 1) * (maxDirFreq / ringCount));

  return (
    <View style={{ alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {rings.map((pct, i) => (
          <Circle key={`ring-${i}`} cx={cx} cy={cy} r={(pct / maxDirFreq) * radius}
            stroke="#d0d0d0" strokeWidth={0.8} fill="none" />
        ))}
        {rings.map((pct, i) => {
          const r = (pct / maxDirFreq) * radius;
          return (
            <SvgText key={`rlbl-${i}`} x={cx + 3} y={cy - r + ringFontSize} fontSize={ringFontSize} fill="#aaa">
              {Math.round(pct * 100)}%
            </SvgText>
          );
        })}
        {directionBins.map((bin, i) => {
          const angle = i * angleStep - 90;
          const tip = polarXY(cx, cy, radius, angle);
          return <Line key={`spoke-${i}`} x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke="#e8e8e8" strokeWidth={0.5} />;
        })}
        {directionBins.map((bin, dirIdx) => {
          const centerAngle = dirIdx * angleStep - 90;
          const half = angleStep / 2;
          let cumPct = 0;
          return (data[bin.label] || []).map((count, spdIdx) => {
            if (count === 0) return null;
            const pct = count / (totalObs || 1);
            const innerR = (cumPct / maxDirFreq) * radius;
            cumPct += pct;
            const outerR = Math.min((cumPct / maxDirFreq) * radius, radius);
            if (outerR <= innerR + 0.5) return null;
            return (
              <Path key={`${dirIdx}-${spdIdx}`}
                d={sectorPath(cx, cy, innerR, outerR, centerAngle - half, centerAngle + half)}
                fill={speedRanges[spdIdx]?.color || '#999'}
                stroke="white" strokeWidth={0.4}
              />
            );
          });
        })}
        {directionBins.map((bin, i) => {
          const angle = i * angleStep - 90;
          const pos = polarXY(cx, cy, radius + labelOffset, angle);
          return (
            <SvgText key={`lbl-${i}`} x={pos.x} y={pos.y}
              textAnchor="middle" alignmentBaseline="middle"
              fontSize={labelFontSize} fontWeight="500" fill="#333">
              {bin.label}
            </SvgText>
          );
        })}
      </Svg>
      <View style={styles.legend}>
        {speedRanges.map((range, i) => (
          <View key={i} style={styles.legendItem}>
            <Legend
              fontColor={ChartStyles.chartTitle.color}
              name={range.end >= 9999 ? `>${range.start} m/s` : `${range.start}–${range.end} m/s`}
              color={range.color}
            />
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  legend: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', marginTop: 8 },
  legendItem: { flexDirection: 'row', alignItems: 'center', marginHorizontal: 5 },
});
